import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  ModalBody,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from 'rehype-sanitize';
import styled from 'styled-components';

import ModalContentContext from './ModalContent.Context';
import { formatBytes, formatDate } from '../../../../utils';
import TaskContainerDatePicker from 'components/Task-Container-Date-Picker';
import axios from 'axios';
import { getAuth } from 'firebase/auth';

const ModalContent = ({ accountId }) => {
  const { content, adminStatus, comments, files, showMoreButton, setShowMoreButton } =
    useContext(ModalContentContext);
  const sendCommentRef = useRef();

  const { t: translate } = useTranslation();
  const implementationDateObj = content?.custom_fields?.find(
    (field) => field.name === 'Data de implementação',
  );
  const implementationDate = implementationDateObj?.value
    ? new Date(+implementationDateObj.value)
    : null;
  const focusesObj = content?.custom_fields?.find((field) => field.name === 'Foco');
  const focusOptions = focusesObj?.type_config?.options;
  const focuses = focusesObj?.value?.map((focusId) => {
    return focusOptions?.find((option) => option.id === focusId);
  });

  const clientResponsibleObj = content?.custom_fields?.find(
    (field) => field.name === 'Responsável do Cliente',
  );
  const clientResponsibleOptions = clientResponsibleObj?.type_config?.options;
  const clientResponsibles = clientResponsibleObj?.value?.map((id) => {
    return clientResponsibleOptions?.find((option) => option.id === id);
  });

  const seeMore = () => {
    const descriptionElem = document.querySelector('#markdown-description');
    const buttonElem = document.querySelector('#show-more-box');

    descriptionElem.classList.toggle('show-more');
    buttonElem.classList.toggle('show-more');

    setShowMoreButton((prevState) => !prevState);
  };

  const handleCommentKeyboard = async (event, id) => {
    const text = event.target.value;
    text
      ? (sendCommentRef.current.style.display = 'flex')
      : (sendCommentRef.current.style.display = 'none');
    if (!text || event.keyCode !== 13 || event.shiftKey) return;

    const auth = getAuth();
    const user = auth.currentUser;
    await user.getIdToken().then((token) => {
      axios.post(
        `${process.env.REACT_APP_API_URL}/addComment?taskId=${id}&accountId=${accountId}`,
        {
          content: text,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    });

    event.target.value = '';
    handleCommentResize(event);

    const scrollElem = document.querySelector('.comment-scroll');
    scrollElem.scrollTop = scrollElem.scrollHeight - scrollElem.clientHeight;
  };

  const handleCommentResize = (event) => {
    const inputElem = event.target;

    inputElem.style.height = 'auto';
    inputElem.style.height = (inputElem.scrollHeight < 150 ? inputElem.scrollHeight : 150) + 'px';
    inputElem.scrollTop = inputElem.scrollHeight;
    window.scrollTo(window.scrollLeft, inputElem.scrollTop + inputElem.scrollHeight);
  };

  const manualCommentResize = (elem) => {
    elem.style.height = 'auto';
    elem.style.height = (elem.scrollHeight < 150 ? elem.scrollHeight : 150) + 'px';
    elem.scrollTop = elem.scrollHeight;
    window.scrollTo(window.scrollLeft, elem.scrollTop + elem.scrollHeight);
  };

  const sanitizedURLs = (string) => {
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
    const newString = string?.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });

    return newString;
  };

  const handleCommentClick = async (event, id) => {
    const inputElem = event.target.previousSibling;
    const text = inputElem.value;
    if (!text) return;

    const auth = getAuth();
    const user = auth.currentUser;
    await user.getIdToken().then((token) => {
      axios.post(
        `${process.env.REACT_APP_API_URL}/addComment?taskId=${id}&accountId=${accountId}`,
        {
          content: text,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    });

    inputElem.value = '';
    manualCommentResize(inputElem);

    const scrollElem = document.querySelector('.comment-scroll');
    scrollElem.scrollTop = scrollElem.scrollHeight - scrollElem.clientHeight;
  };

  const deleteComment = async (taskId, commentId) => {
    const auth = getAuth();
    const user = auth.currentUser;
    await user.getIdToken().then((token) => {
      axios.post(
        `${process.env.REACT_APP_API_URL}/deleteComment?taskId=${taskId}&accountId=${accountId}&commentId=${commentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    });
  };

  return (
    <ModalBody className="d-flex flex-row p-0 justify-content-between">
      <div className="d-flex flex-column my-3 mx-3 ps-3" style={{ width: '62%', maxWidth: '62%' }}>
        <div>
          <h3
            style={{
              overflowWrap: 'break-word',
            }}
          >
            {content?.name}
          </h3>
        </div>
        <div className="d-flex gap-3 mt-2" style={{ color: 'rgba(173, 181, 189, 1)' }}>
          <div className="d-flex flex-column align-items-start gap-1 justify-content-end">
            <LabelDate>{translate('Start date')}</LabelDate>
            <TaskContainerDatePicker
              date={+content?.start_date}
              id={content?.id}
              isDateBegin
              anyDate
              isDisabled
            />
          </div>
          <div className="d-flex flex-column align-items-start gap-1 justify-content-end">
            <LabelDate>{translate('Delivery date')}</LabelDate>
            <TaskContainerDatePicker
              date={+content?.due_date}
              id={content?.id}
              isDateDelivery
              anyDate
              isDisabled
            />
          </div>
          <div className="d-flex flex-column align-items-start gap-1 justify-content-end">
            <LabelDate>{translate('Implementation date')}</LabelDate>
            <TaskContainerDatePicker
              date={implementationDate}
              id={content?.id}
              isDateImplementation
              anyDate
              isDisabled
            />
          </div>
          <div className="d-flex flex-column align-items-start gap-1 justify-content-end">
            <LabelDate>{translate('Pillars')}</LabelDate>
            <div className="d-flex align-items-center justify-content-center">
              <i className="bx bx-purchase-tag-alt fs-4" />
              <span
                className="d-flex flex-wrap mx-2 align-items-center justify-content-center"
                style={{ minHeight: '2.438rem', cursor: 'default' }}
              >
                {focuses?.map((focus) => focus.label)?.join(', ') || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        <div className="d-flex flex-row align-items-start gap-2 mt-2 justify-content-start">
          <i className="bx bxs-user-circle fs-4" />
          <LabelDate>{translate('Client Responsible')}:</LabelDate>
          <span
            className="d-flex flex-wrap align-items-center justify-content-center"
            style={{ cursor: 'default' }}
          >
            {clientResponsibles?.map((responsible) => responsible.label)?.join(', ') || 'N/A'}
          </span>
        </div>
        <hr />
        <div className="modal-task-content">
          {content?.markdown_description && (
            <div
              id="markdown-description"
              className="container markdown-description show-more"
              style={{ overflowWrap: 'break-word', cursor: 'default' }}
              data-color-mode="dark"
            >
              <MDEditor.Markdown
                source={content?.markdown_description?.replace(/(?<!https?:\/\/.*)_(.+)_/g, '*$1*')}
                rehypePlugins={[[rehypeSanitize]]}
                linkTarget="_blank"
              />
              <div
                id="show-more-box"
                className={`markdown-description-fade ${!showMoreButton ? 'show-more' : ''}`}
              >
                <button className="show-more-button" onClick={seeMore}>
                  <i className={`bx bx-chevron-${showMoreButton ? 'up' : 'down'}`} />
                  {translate(`Show ${showMoreButton ? 'Less' : 'More'}`) + '...'}
                </button>
              </div>
            </div>
          )}
          <div className="mt-3">
            {content?.checklists?.[0]?.items.length && (
              <div>
                <h4 className="d-flex align-items-center">
                  <i className="bx bx-list-ul" style={{ color: 'rgba(173, 181, 189, 1)' }} />
                  <p style={{ margin: '0', marginLeft: '.5em', cursor: 'default' }}>
                    {translate('Checklist') +
                      ' ' +
                      content.checklists?.[0]?.resolved +
                      '/' +
                      content.checklists?.[0]?.items.length}
                  </p>
                </h4>
                <hr style={{ margin: '1em 0 1em 0' }} />
                <div className="d-flex flex-column ms-1">
                  {content.checklists?.[0]?.items?.map((check, index) => (
                    <div
                      style={{ lineHeight: '1.1', paddingRight: '1em' }}
                      key={`check-${check.id}`}
                      className="check-div d-flex flex-row align-items-center"
                    >
                      <label key={`check-${index}`} htmlFor={check.id} className="custom-checklist">
                        <input
                          type="checkbox"
                          name={check.id}
                          id={check.id}
                          defaultChecked={check.resolved}
                          defaultValue={check.resolved}
                          disabled
                        />
                        <span id={`check-${check.id}`}>{check.name}</span>
                      </label>
                      <UncontrolledDropdown
                        direction={check.name.length > 20 ? 'start' : 'end'}
                        className="com-message-option"
                        style={{ opacity: '0', cursor: 'auto' }}
                      >
                        <DropdownToggle className="com-message-option" caret tag="span">
                          <button style={{ backgroundColor: 'white' }} className="chk-list-option">
                            <i className="bx bx-dots-horizontal-rounded" />
                          </button>
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItem onClick={() => {}}>
                            <i
                              className="bx bx-pencil"
                              style={{ fontSize: '1.25em', color: 'black' }}
                            />{' '}
                            <span style={{ fontSize: '1.1em', color: 'black' }}>
                              {translate('Edit item')}
                            </span>
                          </DropdownItem>
                          <DropdownItem onClick={() => {}}>
                            <i
                              className="bx bxs-eraser"
                              style={{ fontSize: '1.25em', color: 'black' }}
                            />{' '}
                            <span style={{ fontSize: '1.1em', color: 'black' }}>
                              {translate('Delete item')}
                            </span>
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="mt-3">
            {Boolean(files?.length) && (
              <div>
                <h4 className="d-flex align-items-center">
                  <i className="bx bx-paperclip" style={{ color: 'rgba(173, 181, 189, 1)' }} />
                  <p style={{ margin: '0', marginLeft: '.5em' }}>{translate('Files')}</p>
                </h4>
                <hr style={{ margin: '1em 0 1em 0' }} />
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{translate('File Name')}</th>
                      <th scope="col">{translate('Uploaded')}</th>
                      <th scope="col">{translate('Size')}</th>
                      <th scope="col"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {files?.map((file, index) => (
                      <tr key={index} style={{ cursor: 'auto' }}>
                        <td>{file.title}</td>
                        <td>{new Date(file.date_created).toLocaleDateString()}</td>
                        <td>{formatBytes(file.size)}</td>
                        <td>
                          {
                            <div className="d-flex gap-1">
                              <a
                                className="rounded-circle py-1 px-2"
                                style={{
                                  backgroundColor: '#F2F2F2',
                                  border: 'none',
                                }}
                                href={file.url}
                                download={file.title}
                              >
                                <i className="bx bx-download" style={{ color: 'black' }} />
                              </a>
                            </div>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="comment-wrapper" style={{ width: '38%' }}>
        <div className="comment-title">
          <i className="bx bx-message-square-detail" />
          <h5>{translate('Comments')}</h5>
        </div>
        <div className="comment-scroll">
          {comments?.map((comment) => (
            <div className="comment-message" key={comment.id}>
              <div className={`comment-box ${comment.self ? 'self-box' : 'others-box'}`}>
                <p className="com-box-title">{comment.byName}</p>
                <p
                  className="com-box-content"
                  id={`content-${comment.id}`}
                  dangerouslySetInnerHTML={{
                    __html: sanitizedURLs(comment.content),
                  }}
                />
                <div className="com-box-date">
                  <i className="bx bx-time-five" />
                  <p>{formatDate(new Date(comment.createdAt), translate('date-enUS'), true)}</p>
                </div>
              </div>
              {(comment.self || adminStatus === 'Super Admin') && (
                <UncontrolledDropdown className="com-message-option">
                  <DropdownToggle className="com-message-option" caret tag="span">
                    <button>
                      <i className="bx bx-dots-vertical-rounded" />
                    </button>
                  </DropdownToggle>
                  <DropdownMenu className="menu-shift">
                    <DropdownItem onClick={() => deleteComment(content.id, comment.id)}>
                      {translate('Delete')}
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>
          ))}
          {!comments?.length && (
            <div className="no-comment">
              {translate('There are no comments on the task yet...')}
            </div>
          )}
        </div>
        <div className="comment-input">
          <textarea
            placeholder={translate('Type Comment')}
            onKeyUp={(event) => handleCommentKeyboard(event, content.id)}
            onChange={handleCommentResize}
            rows={1}
          />
          <button
            ref={sendCommentRef}
            style={{ display: 'none' }}
            onClick={(event) => handleCommentClick(event, content.id)}
          >
            {translate('Send')}
            <i className="bx bxs-send" />
          </button>
        </div>
      </div>
    </ModalBody>
  );
};

const LabelDate = styled.span`
  color: #333;
  font-size: 0.8125rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

ModalContent.propTypes = {
  accountId: PropTypes.string,
  taskId: PropTypes.string,
};

export default ModalContent;
